import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { BILLING_BASE } from '@/shared/api/billing-client';
import type { AvailableBalanceResponse } from '@/shared/types/billing';

/**
 * Patient available-balance card. Lives at the top of the Patient Billing
 * tab (PROD-733). The response's `balance[]` array buckets amounts by
 * `subject` (e.g. "Cash", "Insurance") — render each row as a tile.
 *
 * Wire (verified against swagger 2026-05-15 +
 * Flutter `patient_billing_data_source_impl.dart:50`):
 *
 *   GET /Billing/Payment/AvailableBalance?patientId={id}&showArchived=false
 *
 * `showArchived` is hardcoded to `false` to match Flutter; archived rows
 * are surfaced via the dedicated Receipts list, not this card.
 *
 * The api-client injects `userId`, `organizationId`, `practiceid`,
 * `usertimezone` headers — the backend reads tenant context from headers
 * even though swagger lists those as query params. Don't duplicate.
 */
export function usePatientAvailableBalance(patientId: string | null | undefined) {
  return useQuery({
    queryKey: ['patient-available-balance', patientId],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({
        patientId: patientId ?? '',
        showArchived: 'false',
      });
      return get<AvailableBalanceResponse>(
        `${BILLING_BASE}/Payment/AvailableBalance?${qs.toString()}`,
        { signal },
      );
    },
    enabled: !!patientId,
  });
}
